import { useCallback, useEffect, useMemo, useState } from "react";
import { API } from "../../../services/api";
import { ADMIN_API_CONFIG, PATIENT_PAGE_SIZE, PATIENT_STATUS_LABELS, WITHOUT_SUBCATEGORY_FILTER } from "../model/administrationConstants";
import { normalizePatientStatus } from "../model/administrationRules";

const samePatient = (a, b) => String(a) === String(b);

// Un brouillon garde ses saisies mais suit la version, les documents et l'auteur de la fiche rechargée.
export function refreshDraft(draft, patient) {
    const tracked = {
        administrationId: patient.administrationId,
        version: patient.version,
        documents: patient.documents || [],
        updatedAt: patient.updatedAt,
        updatedBy: patient.updatedBy,
    };
    return { ...patient, ...draft, ...tracked, _base: { ...(draft._base || patient), ...tracked } };
}

// Filtre et ordre de la liste des résidents : les inactifs en bas, puis par nom.
export function filterResidentRows(rows, drafts, { search, status, subcategory }) {
    const query = search.trim().toLowerCase();
    const matches = current => {
        if (status === "active" && current.inactive) return false;
        if (status === "inactive" && !current.inactive) return false;
        if (subcategory === WITHOUT_SUBCATEGORY_FILTER && current.productionSubcategory) return false;
        if (subcategory && subcategory !== WITHOUT_SUBCATEGORY_FILTER && current.productionSubcategory !== subcategory) return false;
        if (!query) return true;
        return `${current.nom} ${current.prenom} ${current.prenom} ${current.nom} ${current.chambre || ""} ${current.productionSubcategory || ""} ${current.nir || ""} ${PATIENT_STATUS_LABELS[normalizePatientStatus(current.patientStatus)] || ""}`
            .toLowerCase()
            .includes(query);
    };
    return rows
        .filter(row => matches(drafts[row.patientId] || row))
        .sort((left, right) => {
            const leftCurrent = drafts[left.patientId] || left;
            const rightCurrent = drafts[right.patientId] || right;
            if (Boolean(leftCurrent.inactive) !== Boolean(rightCurrent.inactive)) return leftCurrent.inactive ? 1 : -1;
            return `${leftCurrent.nom || ""} ${leftCurrent.prenom || ""}`.localeCompare(`${rightCurrent.nom || ""} ${rightCurrent.prenom || ""}`, "fr");
        });
}

export function useEhpads({ needsPharmacy, pharmacyId, onError }) {
    const [ehpads, setEhpads] = useState([]);
    const [ehpadId, setEhpadId] = useState("");
    useEffect(() => {
        if (needsPharmacy) return;
        API.get("/ehpads", ADMIN_API_CONFIG)
            .then(response => {
                setEhpads(response.data);
                setEhpadId(current => current || (response.data[0]?.id ? String(response.data[0].id) : ""));
            })
            .catch(() => onError("Impossible de charger les EHPADs."));
    }, [needsPharmacy, onError, pharmacyId]);
    return { ehpads, ehpadId, setEhpadId };
}

// Fenêtres ouvertes sur une fiche : elles suivent la fiche quand elle est rechargée.
export function useResidentModals() {
    const [eventsModal, setEventsModal] = useState(null);
    const [chronicProductRow, setChronicProductRow] = useState(null);
    const [billingHistoryRow, setBillingHistoryRow] = useState(null);
    const [robotHistoryRow, setRobotHistoryRow] = useState(null);
    const [historyRow, setHistoryRow] = useState(null);
    const [identityRow, setIdentityRow] = useState(null);
    const [documentRow, setDocumentRow] = useState(null);
    const [addPatientOpen, setAddPatientOpen] = useState(false);
    const syncPatient = useCallback(patient => {
        setEventsModal(current => (current && samePatient(current.row.patientId, patient.patientId) ? { ...current, row: patient } : current));
        setChronicProductRow(current => (current && samePatient(current.patientId, patient.patientId) ? patient : current));
        setBillingHistoryRow(current => (current && samePatient(current.patientId, patient.patientId) ? patient : current));
    }, []);
    return {
        eventsModal, setEventsModal, chronicProductRow, setChronicProductRow, billingHistoryRow, setBillingHistoryRow,
        robotHistoryRow, setRobotHistoryRow, historyRow, setHistoryRow, identityRow, setIdentityRow,
        documentRow, setDocumentRow, addPatientOpen, setAddPatientOpen, syncPatient,
    };
}

function residentQuery(filters, page) {
    const params = { status: filters.status, page, size: PATIENT_PAGE_SIZE };
    const query = filters.search.trim();
    if (query) params.q = query;
    if (filters.subcategory) params.productionSubcategory = filters.subcategory;
    return params;
}

// Page de résidents : le tableau horizontal charge les fiches complètes, la vue verticale des résumés.
async function fetchResidentPage(ehpadId, viewMode, filters, page) {
    const endpoint = viewMode === "horizontal" ? "patients-page" : "patient-summaries-page";
    const response = await API.get(`/administration/ehpads/${ehpadId}/${endpoint}`, { ...ADMIN_API_CONFIG, params: residentQuery(filters, page) });
    return response.data;
}

export function useResidentPages({ ehpadId, viewMode, filters, onError, setSelectedPatientId }) {
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(false);
    const [page, setPage] = useState(0);
    const [total, setTotal] = useState(0);
    const [hasMore, setHasMore] = useState(false);
    const [loadingMore, setLoadingMore] = useState(false);
    const { search, status, subcategory } = filters;

    const loadRows = useCallback(async ({ append = false, page: requestedPage = 0 } = {}) => {
        if (!ehpadId) {
            setRows([]);
            setTotal(0);
            setHasMore(false);
            return;
        }
        const setBusy = append ? setLoadingMore : setLoading;
        setBusy(true);
        onError("");
        try {
            const pageData = await fetchResidentPage(ehpadId, viewMode, { search, status, subcategory }, requestedPage);
            const content = pageData.content || [];
            setRows(current => append ? [...current, ...content] : content);
            setPage(pageData.page ?? requestedPage);
            setTotal(pageData.totalElements ?? content.length);
            setHasMore(!pageData.last);
            setSelectedPatientId(current => {
                if (content.some(row => samePatient(row.patientId, current))) return current;
                if (append) return current;
                return content[0]?.patientId ? String(content[0].patientId) : "";
            });
        } catch (requestError) {
            onError(requestError.response?.data?.message || "Impossible de charger le suivi administratif.");
        } finally {
            setBusy(false);
        }
    }, [ehpadId, onError, search, setSelectedPatientId, status, subcategory, viewMode]);

    const loadNext = useCallback(() => {
        if (loading || loadingMore || !hasMore) return;
        loadRows({ append: true, page: page + 1 });
    }, [hasMore, loadRows, loading, loadingMore, page]);

    return { rows, setRows, loading, total, setTotal, hasMore, loadingMore, loadRows, loadNext };
}

export function useResidentFilters() {
    const [search, setSearch] = useState("");
    const [status, setStatus] = useState("all");
    const [subcategory, setSubcategory] = useState("");
    const filters = useMemo(() => ({ search, status, subcategory }), [search, status, subcategory]);
    return { filters, search, setSearch, status, setStatus, subcategory, setSubcategory };
}
